import * as React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useShippingRates } from '../hooks/use-shipping-rates';
import { useDeleteShippingRate } from '../hooks/use-delete-shipping-rate';
import { useToggleShippingRateStatus } from '../hooks/use-toggle-shipping-rate-status';
import { useCountriesMap } from '@/lib/hooks/use-countries';
import { EmptyState } from '@/components/organisms/empty-state';
import { DeleteConfirmDialog } from '@/components/molecules/delete-confirm-dialog';
import { Skeleton } from '@/components/atoms/skeleton';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Switch } from '@/components/atoms/switch';
import { ResponsiveDataList, ColumnDef } from '@/components/organisms/responsive-data-list';
import { useTranslation } from 'react-i18next';
import { Plus, Truck, Package, DollarSign, Gift, Clock, Zap, MapPin } from 'lucide-react';
import { formatPrice, getAmountFromPriceObject } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { toast } from 'sonner';
import type { ShippingRate, ShippingRateType, DeliveryType } from '../types/shipping.types';

const typeIconMap: Record<ShippingRateType, React.ElementType> = {
  flat: Truck,
  weight_based: Package,
  price_based: DollarSign,
  free_shipping: Gift,
};

const deliveryTypeIconMap: Record<DeliveryType, React.ElementType> = {
  standard: Truck,
  express: Zap,
  same_day: Clock,
  pickup: MapPin,
};

export function ShippingRatesContainer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: rates, isLoading, error } = useShippingRates();
  const deleteShippingRate = useDeleteShippingRate();
  const toggleStatus = useToggleShippingRateStatus();
  const { getCountryFlag, getCountryName } = useCountriesMap();
  const { currency } = useConfigStore();

  const handleRowClick = (rate: ShippingRate) => {
    navigate(`/shipping/rates/${rate.refId}`);
  };

  const handleDelete = (rate: ShippingRate) => {
    deleteShippingRate.mutate(rate.refId, {
      onSuccess: () => {
        toast.success(t('shipping.rates.deleteSuccess', 'Tarif de livraison supprimé avec succès'));
      },
      onError: () => {
        toast.error(t('shipping.rates.deleteError', 'Erreur lors de la suppression du tarif'));
      },
    });
  };

  // Define table columns for desktop view
  const columns: ColumnDef<ShippingRate>[] = React.useMemo(
    () => [
      {
        id: 'icon',
        header: '',
        cell: (rate) => {
          const rateType = rate.rateCalculation?.type || 'flat';
          const TypeIcon = typeIconMap[rateType] || Truck;
          return (
            <div className="rounded-md bg-muted p-2 w-fit">
              <TypeIcon className="size-4 text-muted-foreground" />
            </div>
          );
        },
        className: 'w-16',
      },
      {
        id: 'name',
        header: t('shipping.rates.name'),
        cell: (rate) => (
          <div>
            <p className="font-medium">{rate.name}</p>
            {rate.description && (
              <p className="text-sm text-muted-foreground line-clamp-1">{rate.description}</p>
            )}
          </div>
        ),
        sortable: true,
      },
      {
        id: 'type',
        header: t('shipping.rates.type'),
        cell: (rate) => {
          const rateType = rate.rateCalculation?.type || 'flat';
          return (
            <Badge variant="outline">
              {t(`shipping.rates.types.${rateType}`, { defaultValue: rateType })}
            </Badge>
          );
        },
      },
      {
        id: 'price',
        header: t('shipping.rates.rate'),
        cell: (rate) => {
          const rateType = rate.rateCalculation?.type || 'flat';
          const priceValue = parseFloat(rate.price) || 0;
          const isFreeShipping = rateType === 'free_shipping' || priceValue === 0;
          return (
            <span className="font-medium">
              {isFreeShipping ? t('shipping.rates.free') : formatPrice(getAmountFromPriceObject(rate.prices, currency, priceValue), currency)}
            </span>
          );
        },
        sortable: true,
      },
      {
        id: 'deliveryType',
        header: t('shipping.rates.deliveryType'),
        cell: (rate) => {
          const DeliveryIcon = deliveryTypeIconMap[rate.deliveryType] || Truck;
          return (
            <div className="flex items-center gap-1">
              <DeliveryIcon className="size-3" />
              <span>{t(`shipping.rates.deliveryTypes.${rate.deliveryType}`, { defaultValue: rate.deliveryType })}</span>
            </div>
          );
        },
      },
      {
        id: 'estimatedDays',
        header: t('shipping.rates.estimatedDays'),
        cell: (rate) =>
          rate.estimatedDeliveryDays ? (
            <span>
              {rate.estimatedDeliveryDays}
              {rate.maxDeliveryDays && rate.maxDeliveryDays !== rate.estimatedDeliveryDays
                ? `-${rate.maxDeliveryDays}`
                : ''} {t('shipping.rates.days')}
            </span>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        id: 'countries',
        header: t('shipping.rates.countries'),
        cell: (rate) =>
          rate.availableCountries && rate.availableCountries.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {rate.availableCountries.slice(0, 2).map((countryCode) => {
                const flag = getCountryFlag(countryCode);
                return (
                  <Badge key={countryCode} variant="secondary" className="text-xs gap-1">
                    {flag && <span>{flag}</span>}
                  </Badge>
                );
              })}
              {rate.availableCountries.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{rate.availableCountries.length - 2}
                </Badge>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        id: 'active',
        header: t('common.active'),
        cell: (rate) => (
          <Switch
            checked={rate.isActive}
            disabled={toggleStatus.isPending}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={() => toggleStatus.mutate(rate.refId)}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (rate) => (
          <DeleteConfirmDialog
            itemName={rate.name}
            onConfirm={() => handleDelete(rate)}
            isLoading={deleteShippingRate.isPending}
          />
        ),
        className: 'w-12',
      },
    ],
    [t, currency, getCountryFlag, deleteShippingRate.isPending, toggleStatus.isPending, toggleStatus.mutate, handleDelete]
  );

  // Card renderer for mobile view
  const renderCard = (rate: ShippingRate, index: number) => {
    const rateType = rate.rateCalculation?.type || 'flat';
    const TypeIcon = typeIconMap[rateType] || Truck;
    const DeliveryIcon = deliveryTypeIconMap[rate.deliveryType] || Truck;
    const priceValue = parseFloat(rate.price) || 0;
    const isFreeShipping = rateType === 'free_shipping' || priceValue === 0;

    return (
      <Card
        key={rate.refId || `rate-${index}`}
        className="cursor-pointer hover:border-primary/50 transition-colors"
        onClick={() => handleRowClick(rate)}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-muted p-2">
              <TypeIcon className="size-4 text-muted-foreground" />
            </div>
            <CardTitle className="text-base font-medium">{rate.name}</CardTitle>
          </div>
          <Switch
            checked={rate.isActive}
            disabled={toggleStatus.isPending}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={() => toggleStatus.mutate(rate.refId)}
          />
        </CardHeader>
        <CardContent className="space-y-2">
          {rate.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">{rate.description}</p>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('shipping.rates.type')}</span>
            <Badge variant="outline">
              {t(`shipping.rates.types.${rateType}`, { defaultValue: rateType })}
            </Badge>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('shipping.rates.rate')}</span>
            <span className="font-medium">
              {isFreeShipping ? t('shipping.rates.free') : formatPrice(getAmountFromPriceObject(rate.prices, currency, priceValue), currency)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('shipping.rates.deliveryType')}</span>
            <div className="flex items-center gap-1">
              <DeliveryIcon className="size-3" />
              <span>{t(`shipping.rates.deliveryTypes.${rate.deliveryType}`, { defaultValue: rate.deliveryType })}</span>
            </div>
          </div>
          {rate.estimatedDeliveryDays && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('shipping.rates.estimatedDays')}</span>
              <span>
                {rate.estimatedDeliveryDays}
                {rate.maxDeliveryDays && rate.maxDeliveryDays !== rate.estimatedDeliveryDays
                  ? `-${rate.maxDeliveryDays}`
                  : ''} {t('shipping.rates.days')}
              </span>
            </div>
          )}
          {rate.availableCountries && rate.availableCountries.length > 0 && (
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground mb-1">
                {rate.availableCountries.length} {t('shipping.rates.countries')}
              </p>
              <div className="flex flex-wrap gap-1">
                {rate.availableCountries.slice(0, 3).map((countryCode) => {
                  const flag = getCountryFlag(countryCode);
                  const name = getCountryName(countryCode);
                  return (
                    <Badge key={countryCode} variant="secondary" className="text-xs gap-1">
                      {flag && <span>{flag}</span>}
                      <span>{name}</span>
                    </Badge>
                  );
                })}
                {rate.availableCountries.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{rate.availableCountries.length - 3}
                  </Badge>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Card skeleton for loading state
  const renderCardSkeleton = () => (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-3/4" />
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      </CardContent>
    </Card>
  );

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('shipping.error')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t('shipping.rates.title')}</h2>
        <Link to="/shipping/rates/new">
          <Button size="sm">
            <Plus className="size-4 mr-2" />
            {t('shipping.rates.add')}
          </Button>
        </Link>
      </div>

      <ResponsiveDataList
        data={rates || []}
        columns={columns}
        renderCard={renderCard}
        renderCardSkeleton={renderCardSkeleton}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        keyExtractor={(rate, index) => rate.refId || `rate-${index}`}
        cardGridClassName="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
        emptyState={
          <EmptyState
            title={t('shipping.rates.emptyTitle')}
            description={t('shipping.rates.emptyDescription')}
            action={
              <Link to="/shipping/rates/new">
                <Button>
                  <Plus className="size-4 mr-2" />
                  {t('shipping.rates.add')}
                </Button>
              </Link>
            }
          />
        }
      />
    </div>
  );
}
